import { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTenantAuth } from '../contexts/TenantAuthContext';
import { tenantApi } from '../lib/tenant-api';
import { isClientAdminEnabled } from '../lib/workspace';

/**
 * `/cap` — a headless launcher. Visiting this route mints the AEO PLATFORM handoff
 * URL (exactly like the dashboard's "Open AEO PLATFORM" button) and redirects the
 * user straight into CAP. No button — just land here and it happens. A CAP loader
 * shows while the launch is in flight.
 */
export default function CapLaunch() {
  const { isAuthenticated, isLoading, admin, tenant } = useTenantAuth();
  const ran = useRef(false);
  const [error, setError] = useState<string | null>(null);

  // Admin-only (e.g. tenant_admin / super_admin) — non-admin roles can't launch CAP.
  const isAdmin = (admin?.role ?? '').toLowerCase().includes('admin');
  const clientAdminDisabled = !isLoading && isAuthenticated && !isClientAdminEnabled(tenant);

  useEffect(() => {
    if (isLoading || !isAuthenticated || !isAdmin || clientAdminDisabled || ran.current) return;
    ran.current = true;
    (async () => {
      try {
        const { url } = await tenantApi.launchAeoPlatform();
        if (!url) {
          setError('AEO PLATFORM is not available yet. Please try again later.');
          return;
        }
        // Full-page redirect into CAP (the token stays server-side, never in the SPA).
        window.location.href = url;
      } catch {
        setError('Could not open AEO PLATFORM. Please try again.');
      }
    })();
  }, [isAuthenticated, isLoading]);

  // Must be signed in to mint the handoff token.
  if (!isLoading && !isAuthenticated) return <Navigate to="/login" replace />;
  if (clientAdminDisabled) return <Navigate to="/" replace />;

  // Admin-only route — non-admins get a clear "no permission" message, not a silent redirect.
  const denied = !isLoading && isAuthenticated && !isAdmin;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white px-8 py-10 shadow-sm">
        {denied ? (
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-50">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <p className="text-base font-semibold text-gray-900">You don't have permission</p>
            <p className="text-sm text-gray-500">
              The AEO platform is available to admins only. Contact your administrator if you need access.
            </p>
            <a href="/" className="mt-1 text-sm font-medium text-indigo-600 hover:text-indigo-700">
              Back to dashboard
            </a>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4" role="status" aria-live="polite">
            {/* CAP loader: monogram inside a spinning ring */}
            <div className="relative flex h-16 w-16 items-center justify-center">
              <span className="absolute inset-0 animate-spin rounded-full border-2 border-indigo-100 border-t-indigo-600" />
              <span className="inline-flex h-8 items-center justify-center rounded-lg bg-indigo-600 px-2 text-sm font-extrabold text-white">
                CAP
              </span>
            </div>
            <p className={`text-sm ${error ? 'text-red-600' : 'text-gray-500'}`}>
              {error ?? 'Launching AEO Platform…'}
            </p>
            {error ? (
              <a href="/" className="text-sm font-medium text-indigo-600 hover:text-indigo-700">
                Back to dashboard
              </a>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
